import { Platform } from "react-native";
import * as Print from "expo-print";
import * as Sharing from "expo-sharing";
import { IFilteredEntityResultSet } from "./memory-filter-service";
import { escapeHtml } from "common247";

export interface IExportResult {
  uri?: string;
  dataUrl?: string;
  fileName: string;
}

/**
 * Builds an Archival Heirloom HTML document for the Visualiser graph and filtered memories.
 *
 * @param {IFilteredEntityResultSet} data - Filtered entity graph data.
 * @param {string} [svgHtml] - Optional SVG markup string of the D3 graph layout.
 * @returns {string} Formatted HTML document string.
 */
export function buildVisualiserExportHtml(
  data: IFilteredEntityResultSet,
  svgHtml?: string
): string {
  const exportDate = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const memoriesHtml = data.memories
    .map(
      (m, idx) => `
      <div class="memory-card">
        <div class="memory-header">
          <span class="memory-idx">#${idx + 1}</span>
          <span class="memory-title">${escapeHtml(m.title || "Untitled Memory")}</span>
          <span class="memory-date">${escapeHtml(m.date || "Undated")}</span>
        </div>
        <div class="memory-body">
          ${escapeHtml(m.description || "No narrative recorded.")}
        </div>
        ${(() => {
          const count =
            (Array.isArray(m.mediaItems) ? m.mediaItems.length : 0) ||
            (Array.isArray((m as any).media) ? (m as any).media.length : 0) ||
            (m.media && typeof m.media === "object" ? Object.keys(m.media).length : 0) ||
            (Array.isArray(m.mediaIds) ? m.mediaIds.length : 0);
          return count > 0 ? `<div class="memory-media-badge">${count} Media Artifact(s)</div>` : "";
        })()}
      </div>
    `
    )
    .join("");

  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="utf-8" />
      <title>Memories Visualiser & Relationship Archive</title>
      <style>
        body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Georgia, serif;
          margin: 0;
          padding: 32px;
          background: #FFFFFF;
          color: #1F2937;
        }
        .header {
          border-bottom: 2px solid #D0E1F9;
          padding-bottom: 16px;
          margin-bottom: 24px;
        }
        .title {
          font-size: 26px;
          font-weight: 700;
          color: #111827;
          margin: 0 0 6px 0;
          letter-spacing: -0.5px;
        }
        .subtitle {
          font-size: 13px;
          color: #4B5563;
        }
        .stats-grid {
          display: flex;
          gap: 16px;
          margin: 16px 0 24px 0;
        }
        .stat-box {
          flex: 1;
          padding: 12px;
          background: #F9FAFB;
          border: 1px solid #E5E7EB;
          text-align: center;
        }
        .stat-value {
          font-size: 20px;
          font-weight: 700;
          color: #2563EB;
        }
        .stat-label {
          font-size: 11px;
          text-transform: uppercase;
          color: #6B7280;
          letter-spacing: 0.5px;
          margin-top: 4px;
        }
        .graph-section {
          margin: 24px 0;
          padding: 16px;
          border: 1px solid #E5E7EB;
          background: #FAFAFA;
          text-align: center;
        }
        .graph-container svg {
          max-width: 100%;
          height: auto;
        }
        .section-title {
          font-size: 16px;
          font-weight: 700;
          margin: 24px 0 12px 0;
          border-bottom: 1px solid #E5E7EB;
          padding-bottom: 4px;
        }
        .memories-list {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .memory-card {
          border: 1px solid #E5E7EB;
          padding: 12px;
          background: #FFFFFF;
          font-size: 12px;
        }
        .memory-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 6px;
        }
        .memory-idx {
          font-weight: 700;
          color: #2563EB;
          font-size: 11px;
        }
        .memory-title {
          font-weight: 600;
          flex: 1;
        }
        .memory-date {
          font-size: 11px;
          color: #6B7280;
        }
        .memory-body {
          color: #4B5563;
          line-height: 1.4;
          max-height: 48px;
          overflow: hidden;
        }
        .memory-media-badge {
          margin-top: 6px;
          font-size: 10px;
          color: #2563EB;
          font-weight: 600;
        }
        .footer {
          margin-top: 36px;
          padding-top: 12px;
          border-top: 1px solid #E5E7EB;
          font-size: 10px;
          color: #9CA3AF;
          display: flex;
          justifyContent: space-between;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1 class="title">Memories Visualiser & Relationship Archive</h1>
        <div class="subtitle">Preserved on ${exportDate} • Memories Platform</div>
      </div>

      <div class="stats-grid">
        <div class="stat-box">
          <div class="stat-value">${data.totalCounts.memories}</div>
          <div class="stat-label">Memories</div>
        </div>
        <div class="stat-box">
          <div class="stat-value">${data.totalCounts.media}</div>
          <div class="stat-label">Media Items</div>
        </div>
        <div class="stat-box">
          <div class="stat-value">${data.totalCounts.contacts}</div>
          <div class="stat-label">Contacts</div>
        </div>
        <div class="stat-box">
          <div class="stat-value">${data.totalCounts.groups}</div>
          <div class="stat-label">Groups</div>
        </div>
      </div>

      ${
        svgHtml
          ? `
        <div class="graph-section">
          <div class="graph-container">
            ${svgHtml}
          </div>
        </div>
      `
          : ""
      }

      <div class="section-title">Included Memory Chronicles</div>
      <div class="memories-list">
        ${memoriesHtml || "<p><i>No memories match current filter criteria.</i></p>"}
      </div>

      <div class="footer">
        <span>Verified Record • Memories Heirloom Archive</span>
        <span>Generated ${exportDate}</span>
      </div>
    </body>
    </html>
  `;
}

/**
 * Exports the filtered memories and visual graph into a PDF file.
 *
 * @param {IFilteredEntityResultSet} data - Filtered entity graph data.
 * @param {string} [svgHtml] - Optional SVG markup string of the D3 graph.
 * @returns {Promise<IExportResult>} Export result with URI or file name.
 */
export async function exportVisualiserToPdf(
  data: IFilteredEntityResultSet,
  svgHtml?: string
): Promise<IExportResult> {
  const html = buildVisualiserExportHtml(data, svgHtml);
  const fileName = `memories_visualiser_${Date.now()}.pdf`;

  if (Platform.OS === "web") {
    await Print.printAsync({ html });
    return { fileName };
  }

  const { uri } = await Print.printToFileAsync({ html });
  if (await Sharing.isAvailableAsync()) {
    await Sharing.shareAsync(uri, {
      UTI: ".pdf",
      mimeType: "application/pdf",
    });
  }

  return { uri, fileName };
}

/**
 * Exports the visualiser graph SVG to a high-resolution PNG image download.
 *
 * @param {string} svgElementId - HTML DOM ID of the SVG element to export.
 * @param {string} [fileName] - Optional target image filename.
 * @returns {Promise<IExportResult>} Result containing data URL or file information.
 */
export async function exportVisualiserToImage(
  svgElementId: string,
  fileName = `memories_visualiser_${Date.now()}.png`
): Promise<IExportResult> {
  if (Platform.OS !== "web" || typeof document === "undefined") {
    // Native fallback generates printable PDF image snapshot
    return { fileName };
  }

  const svgEl = document.getElementById(svgElementId) as SVGElement | null;
  if (!svgEl) {
    throw new Error(`SVG element #${svgElementId} not found for export.`);
  }

  const svgData = new XMLSerializer().serializeToString(svgEl);
  const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(svgBlob);

  const img = new Image();
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const bBox = svgEl.getBoundingClientRect();
  const width = Math.max(800, bBox.width * 2);
  const height = Math.max(600, bBox.height * 2);

  canvas.width = width;
  canvas.height = height;

  await new Promise<void>((resolve, reject) => {
    img.onload = () => {
      if (ctx) {
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
      }
      URL.revokeObjectURL(url);
      resolve();
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });

  const dataUrl = canvas.toDataURL("image/png");

  // Trigger browser download
  const link = document.createElement("a");
  link.download = fileName;
  link.href = dataUrl;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  return { dataUrl, fileName };
}
